import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '@/store/store';
import {
  fetchBalance,
  fetchTransactions,
  fetchWalletStats,
  fetchWalletAnalytics,
} from '@/store/slices/walletSlice';
import { FiDollarSign, FiTrendingUp, FiTrendingDown, FiClock, FiArrowRight, FiCreditCard, FiArrowLeft } from 'react-icons/fi';
import {
  BalanceCard,
  QuickStatsCard,
  TransactionList,
  DepositModal,
  WithdrawModal,
  TransferModal,
  TransactionDetailsModal,
} from '@/components/wallet';
import { useCurrency } from '@/contexts/CurrencyContext';

const WalletPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { balance, transactions, stats, loading } = useSelector((state: RootState) => state.wallet);
  const { userCurrency: selectedCurrency } = useCurrency();

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);

  useEffect(() => {
    dispatch(fetchBalance(selectedCurrency as any));
    dispatch(fetchTransactions({ filters: {}, page: 1, limit: 5 }));
    dispatch(fetchWalletStats());
    dispatch(fetchWalletAnalytics({ startDate: undefined, endDate: undefined }));
  }, [dispatch, selectedCurrency]);

  const handleRefreshBalance = () => {
    dispatch(fetchBalance(selectedCurrency as any));
  };

  const handleTransactionClick = (transaction: any) => {
    setSelectedTransaction(transaction);
    setShowTransactionModal(true);
  };

  const recentTransactions = transactions?.transactions.slice(0, 5) || [];

  return (
    <>
      <Helmet>
        <title>My Wallet - SideGO</title>
        <meta name="description" content="Manage your SideGO wallet, deposits, withdrawals, and transactions" />
      </Helmet>

      <div className="relative py-1 sm:py-2">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-gradient-to-b from-cyan-100/60 to-transparent dark:from-cyan-900/20" />

        <div className="mb-3 sm:mb-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>
        </div>

        <div className="mb-4 overflow-hidden rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/85 sm:mb-8 sm:p-6">
          <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              Wallet Center
            </span>
            <span className="inline-flex items-center whitespace-nowrap rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700 dark:border-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
              Live Balance
            </span>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 dark:text-white sm:text-3xl">My Wallet</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-gray-400 sm:text-base">
            Manage your funds, deposits, and withdrawals
          </p>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 sm:mb-8 sm:gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <BalanceCard
              balance={balance}
              loading={loading}
              onRefresh={handleRefreshBalance}
              showActions={true}
              onDeposit={() => setShowDepositModal(true)}
              onWithdraw={() => setShowWithdrawModal(true)}
              onTransfer={() => setShowTransferModal(true)}
            />
          </div>

          <div className="lg:col-span-1">
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-5">
              <h3 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white sm:mb-4 sm:text-lg">
                <FiCreditCard className="h-4 w-4 text-primary-600" />
                Quick Actions
              </h3>
              <div className="space-y-2.5 sm:space-y-3">
                <button
                  onClick={() => navigate('/dashboard/wallet/transactions')}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-left transition-colors hover:border-primary-400 hover:bg-primary-50/70 dark:border-gray-700 dark:hover:bg-primary-900/20 sm:px-4 sm:py-3"
                >
                  <p className="flex items-center justify-between font-medium text-slate-900 dark:text-white">
                    <span>View All Transactions</span>
                    <FiArrowRight className="h-4 w-4 text-primary-500" />
                  </p>
                  <p className="mt-1 text-xs text-slate-600 dark:text-gray-400 sm:text-sm">
                    See complete transaction history
                  </p>
                </button>
                <button
                  onClick={() => navigate('/dashboard/wallet/withdrawals')}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-left transition-colors hover:border-primary-400 hover:bg-primary-50/70 dark:border-gray-700 dark:hover:bg-primary-900/20 sm:px-4 sm:py-3"
                >
                  <p className="flex items-center justify-between font-medium text-slate-900 dark:text-white">
                    <span>Withdrawal History</span>
                    <FiArrowRight className="h-4 w-4 text-primary-500" />
                  </p>
                  <p className="mt-1 text-xs text-slate-600 dark:text-gray-400 sm:text-sm">
                    Track your withdrawal requests
                  </p>
                </button>
                <button
                  onClick={() => navigate('/dashboard/wallet/settings')}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-left transition-colors hover:border-primary-400 hover:bg-primary-50/70 dark:border-gray-700 dark:hover:bg-primary-900/20 sm:px-4 sm:py-3"
                >
                  <p className="flex items-center justify-between font-medium text-slate-900 dark:text-white">
                    <span>Wallet Settings</span>
                    <FiArrowRight className="h-4 w-4 text-primary-500" />
                  </p>
                  <p className="mt-1 text-xs text-slate-600 dark:text-gray-400 sm:text-sm">
                    Manage payment methods & preferences
                  </p>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 sm:mb-8 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
          <QuickStatsCard
            title="Total Earned"
            value={stats?.totalEarned || 0}
            currency={selectedCurrency}
            icon={FiDollarSign}
            iconColor="text-green-600"
            loading={loading}
          />
          <QuickStatsCard
            title="Total Withdrawn"
            value={stats?.totalWithdrawn || 0}
            currency={selectedCurrency}
            icon={FiTrendingDown}
            iconColor="text-red-600"
            loading={loading}
          />
          <QuickStatsCard
            title="This Month"
            value={stats?.currentMonthEarnings || 0}
            currency={selectedCurrency}
            icon={FiTrendingUp}
            iconColor="text-blue-600"
            loading={loading}
          />
          <QuickStatsCard
            title="Pending Payouts"
            value={stats?.pendingPayouts || 0}
            currency={selectedCurrency}
            icon={FiClock}
            iconColor="text-yellow-600"
            loading={loading}
          />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-5">
          <div className="mb-3 flex items-center justify-between sm:mb-5">
            <h2 className="text-base font-bold text-slate-900 dark:text-white sm:text-xl">
              Recent Transactions
            </h2>
          </div>

          <TransactionList
            transactions={recentTransactions}
            loading={loading}
            onTransactionClick={handleTransactionClick}
            showViewAll={true}
            viewAllLink="/dashboard/wallet/transactions"
          />
        </div>
      </div>

      <DepositModal isOpen={showDepositModal} onClose={() => setShowDepositModal(false)} />
      <WithdrawModal isOpen={showWithdrawModal} onClose={() => setShowWithdrawModal(false)} />
      <TransferModal isOpen={showTransferModal} onClose={() => setShowTransferModal(false)} />
      <TransactionDetailsModal
        transaction={selectedTransaction}
        isOpen={showTransactionModal}
        onClose={() => {
          setShowTransactionModal(false);
          setSelectedTransaction(null);
        }}
      />
    </>
  );
};

export default WalletPage;
